/**
 * Customer Service - Frontend service for customer operations
 */

import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Search customers by name, phone, or email
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Array of matching customers
 */
export const searchCustomers = async (searchTerm) => {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }
    const response = await api.get("/customers/search", {
      params: { q: searchTerm.trim() },
    });
    return response.data || [];
  } catch (error) {
    logError("Error searching customers", {
      searchTerm,
      error: error.message,
      status: error.response?.status,
    });
    return [];
  }
};

/**
 * Get customer by ID
 * @param {string} customerId - Customer ID
 * @param {boolean} includeRelations - Whether to include related leads/quotes/invoices
 * @returns {Promise<Object>} Customer data
 */
export const getCustomer = async (customerId, includeRelations = false) => {
  try {
    const params = {};
    if (includeRelations) {
      params.includeRelations = "true";
    }
    const response = await api.get(`/customers/${customerId}`, { params });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to get customer",
      data: null,
    };
  }
};

/**
 * Get all customers with optional filters
 * @param {Object} filters - Filter options
 * @param {string} [filters.phone] - Filter by phone
 * @param {string} [filters.email] - Filter by email
 * @param {number} [limit=50] - Maximum number of results
 * @param {number} [skip=0] - Number of results to skip
 * @returns {Promise<Object>} Customers data
 */
export const getCustomers = async (filters = {}, limit = 50, skip = 0) => {
  try {
    const params = {
      limit,
      skip,
      ...filters,
    };
    const response = await api.get("/customers", { params });
    return {
      success: true,
      data: response.data || [],
      count: response.count || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to get customers",
      data: [],
      count: 0,
    };
  }
};

/**
 * Create a new customer
 * @param {Object} customerData - Customer data
 * @param {string} customerData.name - Customer name (required)
 * @param {string} [customerData.phone] - Phone number (optional)
 * @param {string} [customerData.email] - Email address (optional)
 * @param {string} [customerData.address] - Address (optional)
 * @returns {Promise<Object>} Created customer
 */
export const createCustomer = async (customerData) => {
  try {
    const data = {
      name: customerData.name,
      phone: customerData.phone || null,
      email: customerData.email || null,
      address: customerData.address || null,
    };

    const response = await api.post("/customers", data);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to create customer",
    };
  }
};

/**
 * Update customer
 * @param {string} customerId - Customer ID
 * @param {Object} updates - Update data
 * @param {string} [updates.name] - Customer name
 * @param {string} [updates.phone] - Phone number
 * @param {string} [updates.email] - Email address
 * @param {string} [updates.address] - Address
 * @returns {Promise<Object>} Updated customer
 */
export const updateCustomer = async (customerId, updates) => {
  try {
    const response = await api.patch(`/customers/${customerId}`, updates);
    return {
      success: true,
      data: response.data || response,
      message: response.message || "Customer updated successfully",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to update customer",
    };
  }
};

